import {Button, useMediaQuery, Tooltip } from '@material-ui/core';
import ChecksDrawer from 'Agent/Claims/components/ChecksDrawer';
import OutlinedInfoIcon from '@mui/icons-material/InfoOutlined';
import {
  changeClaimChecks,
  changeClaimFilters,
  changeClaims,
  changeSort,
  clearClaims,
  clearSort,
  EmptyClaimFilter,
  extractPolicyClaims,
  getClaimChecksPost,
  getClaims,
  sortClaims,
} from 'Agent/Claims/ducks/claim';
import {theme} from 'Core/components/WithTheme';
import {useBreadcrumbs} from 'Core/hooks/useBreadcrumbs';
import {Empty, Loaded, toPage} from 'Core/models/Async';
import {toUndefined} from 'fp-ts/lib/Option';
import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {RouteComponentProps, useLocation} from 'react-router-dom';
import {ReducerState} from 'rootReducer';
import FileDownloadButton, {FileExtension} from 'Shared/components/FileDownloadButton';
import FileDownloadFab from 'Shared/components/FileDownloadFab';
import Column from 'Shared/components/PagedView/Column';
import PagedView from 'Shared/components/PagedView/PagedView';
import {AgencyFilters} from 'Shared/models/AgencyFilters';
import {addFilterParameters} from 'Shared/utilities/addFilterParameters';
import {toFormattedDateAsUTC} from 'Shared/utilities/DateFormatter';
import {toMoneyValue} from 'Shared/utilities/FormatMoney';
import {prop} from 'Shared/utilities/prop';
import qs from 'query-string';
import {DataSource} from 'Shared/utilities/DataSourceUtility';

type ClaimProps = {
  selectedAgency: AgencyFilters;
} & RouteComponentProps<{policyNumber: string}>;

export const Claims = (props: ClaimProps) => {
  const dispatch = useDispatch();

  useBreadcrumbs('Claims', props.match.url);
  const {claims, sort, filter} = useSelector(mapStateToProps);
  const {selectedAgency} = props;
  const {currentAgentNumbers} = useSelector((state: ReducerState) => state.user.profile);
  const [selectedClaimNumber, setSelectedClaimNumber] = useState('');
  const {search} = useLocation();
  const parsedQueryParams = qs.parse(search);
  const [tooltipIsOpen, setTooltipIsOpen] = React.useState({});
  const claimStatusClicked = (label: string, value: Boolean) => {
    Object.keys(tooltipIsOpen).forEach(key => (tooltipIsOpen[key] = false));
    setTooltipIsOpen(values => ({
      ...values,
      [label]: value,
    }));
  };

  const source =
    parsedQueryParams.source !== null && typeof parsedQueryParams.source == 'string'
      ? parsedQueryParams.source
      : String(DataSource.LEGACY);

  useEffect(() => {
    dispatch(
      getClaims(
        currentAgentNumbers,
        selectedAgency.isNewYork,
        selectedAgency.encryptedIdentifier,
        props.match.params.policyNumber,
        undefined,
        filter,
        source
      )
    );
  }, [filter]);

  useEffect(() => {
    const policyClaimsData = extractPolicyClaims(claims);
    if (policyClaimsData !== undefined) {
      dispatch(changeClaims(new Loaded(toPage(sortClaims(policyClaimsData, sort)))));
    }
  }, [sort]);

  useEffect(
    () => () => {
      dispatch(clearClaims());
      dispatch(changeClaimFilters(EmptyClaimFilter));
      dispatch(changeClaimChecks(new Empty()));
      dispatch(clearSort());
    },
    [clearClaims]
  );

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const exportToCsvProps = {
    startIcon: <i className="fa fa-2x fa-file-download" />,
    extension: FileExtension.CSV,
    downloadedFileName: 'FilteredClaimsDetails',
    relativeFileLocation: `/api/claims/v1/policies/${props.match.params.policyNumber}/csv${addFilterParameters({
      ...filter,
      agentNumbers: currentAgentNumbers.map(prop('value')),
      isNewYork: selectedAgency.isNewYork,
      encryptedIdentifier: selectedAgency.encryptedIdentifier,
      eisFlag: source == '1' ? true : false,
    })}`,
    children: 'Export To CSV',
  };

  return (
    <>
      <PagedView
        pagedViewKey="claims"
        eventAction="agentPoliciesViewClaimsSettingFilter"
        data={claims}
        sort={toUndefined(sort)}
        onChangeSort={sort => dispatch(changeSort(sort))}
        loadMore={page =>
          dispatch(
            getClaims(
              currentAgentNumbers,
              selectedAgency.isNewYork,
              selectedAgency.encryptedIdentifier,
              props.match.params.policyNumber,
              page,
              filter
            )
          )
        }
        actions={
          isMobile ? (
            <FileDownloadFab variant="extended" color="primary" displayTooltip {...exportToCsvProps} />
          ) : (
            <FileDownloadButton variant="contained" color="primary" displayTooltip {...exportToCsvProps} />
          )
        }
      >
        {columnProps => (
          <>
            <Column {...columnProps} title="Claim Number" sortColumnKey="maskedClaimNumber" default ddprivacy="mask">
              {claim => claim.maskedClaimNumber}
            </Column>
            <Column {...columnProps} title="Received Date" sortColumnKey="claimReceiptDate" default>
              {claim => toFormattedDateAsUTC(claim.claimReceiptDate)}
            </Column>
            <Column {...columnProps} title="Claimant" sortColumnKey="claimantName" default ddprivacy="mask">
              {claim => claim.claimantName}
            </Column>
            <Column {...columnProps} title="Service From Date" sortColumnKey="serviceFromDate" default>
              {claim => (claim?.serviceFromDate !== null ? toFormattedDateAsUTC(claim.serviceFromDate) : '')}
            </Column>
            <Column {...columnProps} title="Service Through Date" sortColumnKey="serviceThroughDate" default>
              {claim => props.match.params.policyNumber?.startsWith('PL') ? 'N/A' : (claim?.serviceThroughDate !== null ? toFormattedDateAsUTC(claim.serviceThroughDate) : '')}
            </Column>
            <Column {...columnProps} title="Total Paid Amount" sortColumnKey="totalPaidAmount" default>
              {claim => toMoneyValue(claim.totalPaidAmount)}
            </Column>
            <Column {...columnProps} title="Status"  default>
              {claim =>
                claim.claimStatus?.split('-')[0].includes('CLOSED') ? (
                  <div className="claims-display-name">
                    {claim.claimStatus
                      ?.split('-')[1]
                      .trim()
                      .split('_')
                      .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
                      .join(' ')}
                  </div>
                ) : (
                  <div className="claims-display-name">
                    {claim.claimStatus
                      ?.split('-')[0]
                      .trim()
                      .split('_')
                      .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
                      .join(' ')}
                    {claim.claimStatus?.split('-')[1] && (
                      <Tooltip
                        title={claim.claimStatus.slice(claim.claimStatus.indexOf('-') + 1)}
                        aria-label="info"
                        open={claim.maskedClaimNumber !== undefined && tooltipIsOpen[claim.maskedClaimNumber]}
                        onClick={() =>
                          claim.maskedClaimNumber !== undefined &&
                          claimStatusClicked(claim.maskedClaimNumber, !tooltipIsOpen[claim.maskedClaimNumber])
                        }
                      >
                        <OutlinedInfoIcon />
                      </Tooltip>
                    )}
                  </div>
                )
              }
            </Column>
            <Column {...columnProps} title="" isActions default>
              {claim =>
                claim.hasChecks ? (
                  <Button
                    style={{width: '100%'}}
                    color="primary"
                    onClick={() => {
                      dispatch(getClaimChecksPost(claim, selectedAgency.encryptedIdentifier));
                      setSelectedClaimNumber(claim.maskedClaimNumber!);
                    }}
                  >
                    View Checks
                  </Button>
                ) : (
                  <div style={{width: '100%', textAlign: 'center'}}>No Checks Available</div>
                )
              }
            </Column>
          </>
        )}
      </PagedView>
      <ChecksDrawer
        claimNumber={selectedClaimNumber}
        open={Boolean(selectedClaimNumber)}
        onClose={() => {
          dispatch(changeClaimChecks(new Empty()));
          setSelectedClaimNumber('');
        }}
      />
    </>
  );
};

const mapStateToProps = (state: ReducerState) => ({
  claims: state.claim.claims,
  filter: state.claim.claimsFilter,
  sort: state.claim.sort,
});
export default Claims;
